/**
 * BlockAuthoringPaletteShell — Phase K.1.
 *
 * Floating, draggable, dockable Block Authoring Palette UI shell. The
 * shell owns:
 *
 *   - 3 tabs: Параметры / Действия / Наборы параметров
 *   - 10 parameter palette items + 8 action palette items + a small
 *     placeholder list for parameter sets
 *   - drag-and-click handlers that fire the placeholder
 *     `block-authoring/begin-place` callback (parent dispatches the
 *     real draft session in Phase K.2 / K.3)
 *
 * Phase K.1 deliberately does NOT wire any draft state machine — the
 * onBeginPlace callback receives a typed `BlockAuthoringPlacementKind`
 * descriptor but the shell stays UI-pure.
 *
 * Persistence: the shell's open / position / activeTab / docked
 * preferences are restored from `localStorage` under the key
 * "edit-dwg.block-authoring-palette". The parent (App.tsx) is
 * responsible for keeping the in-memory state in sync via the
 * onPersist callback so reducer dispatches stay deterministic.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from "react";

import { useT } from "../i18n";

import {
  BlockAuthoringIcon,
  type ActionIconKind,
  type ParameterIconKind,
} from "./BlockAuthoringPaletteIcons";
import {
  BlockAuthoringPaletteShellTabs,
  type BlockAuthoringPaletteTab,
} from "./BlockAuthoringPaletteShellTabs";

/**
 * Localised key for `localStorage`. Visible in the persisted state so
 * an audit can confirm the right preference is being read / written.
 */
export const BLOCK_AUTHORING_PALETTE_STORAGE_KEY =
  "edit-dwg.block-authoring-palette";

/**
 * Full set of palette items the user can place. The `kind` mirrors the
 * matrix in `docs/autocad-parity/block-authoring-spec.md` §1.1; the
 * Phase K.2 / K.3 draft state machines key off this same string.
 */
export type BlockAuthoringPlacementKind =
  | { tab: "parameters"; id: ParameterIconKind }
  | { tab: "actions"; id: ActionIconKind }
  | { tab: "parameter-sets"; id: ParameterSetId };

/**
 * Quick-pick parameter+action combos. Phase K.4 will wire each id to
 * the existing `expandParameterSet()` helper from
 * `BlockAuthoringPaletteParameterSets.tsx`. For Phase K.1 the shell
 * only renders the 4 most common combos so the tab is non-empty; the
 * remaining 11 combos land alongside K.4.
 */
export type ParameterSetId =
  | "linear-move"
  | "linear-stretch"
  | "polar-move"
  | "rotation-set";

interface ParameterItem {
  readonly id: ParameterIconKind;
  readonly labelKey: string;
}

interface ActionItem {
  readonly id: ActionIconKind;
  readonly labelKey: string;
}

interface ParameterSetItem {
  readonly id: ParameterSetId;
  readonly labelKey: string;
}

const PARAMETER_ITEMS: readonly ParameterItem[] = [
  { id: "point", labelKey: "block_authoring.params.point" },
  { id: "linear", labelKey: "block_authoring.params.linear" },
  { id: "polar", labelKey: "block_authoring.params.polar" },
  { id: "xy", labelKey: "block_authoring.params.xy" },
  { id: "rotation", labelKey: "block_authoring.params.rotation" },
  { id: "alignment", labelKey: "block_authoring.params.alignment" },
  { id: "flip", labelKey: "block_authoring.params.flip" },
  { id: "visibility", labelKey: "block_authoring.params.visibility" },
  { id: "lookup", labelKey: "block_authoring.params.lookup" },
  { id: "basepoint", labelKey: "block_authoring.params.basepoint" },
];

const ACTION_ITEMS: readonly ActionItem[] = [
  { id: "move", labelKey: "block_authoring.actions.move" },
  { id: "scale", labelKey: "block_authoring.actions.scale" },
  { id: "stretch", labelKey: "block_authoring.actions.stretch" },
  { id: "polar-stretch", labelKey: "block_authoring.actions.polar-stretch" },
  { id: "rotation", labelKey: "block_authoring.actions.rotation" },
  { id: "flip", labelKey: "block_authoring.actions.flip" },
  { id: "array", labelKey: "block_authoring.actions.array" },
  { id: "lookup", labelKey: "block_authoring.actions.lookup" },
];

const PARAMETER_SET_ITEMS: readonly ParameterSetItem[] = [
  { id: "linear-move", labelKey: "block_authoring.sets.linear-move" },
  { id: "linear-stretch", labelKey: "block_authoring.sets.linear-stretch" },
  { id: "polar-move", labelKey: "block_authoring.sets.polar-move" },
  { id: "rotation-set", labelKey: "block_authoring.sets.rotation-set" },
];

/**
 * MIME type used during drag-and-drop to carry the placement kind from
 * a palette item to the canvas drop target. Phase K.2 / K.3 will read
 * the same key from the dataTransfer.
 */
export const BLOCK_AUTHORING_DRAG_MIME = "application/x-edit-dwg-block-authoring";

export interface BlockAuthoringPaletteShellState {
  open: boolean;
  position: { x: number; y: number };
  activeTab: BlockAuthoringPaletteTab;
  docked: "right" | "float";
}

export const DEFAULT_BLOCK_AUTHORING_PALETTE_STATE: BlockAuthoringPaletteShellState =
  {
    open: false,
    position: { x: 1200, y: 100 },
    activeTab: "parameters",
    docked: "float",
  };

/**
 * Read the persisted palette preferences from `localStorage`. Returns
 * `null` when no state has been persisted or the payload is unreadable
 * — callers fall back to {@link DEFAULT_BLOCK_AUTHORING_PALETTE_STATE}.
 *
 * Exported so the host (App.tsx) can hydrate `useState` lazily without
 * re-implementing the parsing rules.
 */
export function readPersistedBlockAuthoringPaletteState():
  | BlockAuthoringPaletteShellState
  | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      BLOCK_AUTHORING_PALETTE_STORAGE_KEY,
    );
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    const open = typeof obj.open === "boolean" ? obj.open : false;
    const pos = obj.position as { x?: unknown; y?: unknown } | undefined;
    const x = typeof pos?.x === "number" ? pos.x : 1200;
    const y = typeof pos?.y === "number" ? pos.y : 100;
    const tab = (
      typeof obj.activeTab === "string" ? obj.activeTab : "parameters"
    ) as BlockAuthoringPaletteTab;
    const dockedRaw = typeof obj.docked === "string" ? obj.docked : "float";
    const docked: "right" | "float" =
      dockedRaw === "right" ? "right" : "float";
    const validTab: BlockAuthoringPaletteTab =
      tab === "parameters" || tab === "actions" || tab === "parameter-sets"
        ? tab
        : "parameters";
    return {
      open,
      position: { x, y },
      activeTab: validTab,
      docked,
    };
  } catch {
    return null;
  }
}

/**
 * Persist the current palette state. No-op on the server (jsdom-safe).
 */
export function persistBlockAuthoringPaletteState(
  state: BlockAuthoringPaletteShellState,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      BLOCK_AUTHORING_PALETTE_STORAGE_KEY,
      JSON.stringify(state),
    );
  } catch {
    // ignore — localStorage may be unavailable in private browsing.
  }
}

export interface BlockAuthoringPaletteShellProps {
  /** Visible-or-not. Driven by the host (App.tsx) so the ribbon can toggle. */
  open: boolean;
  /** Floating position in CSS px. Updated by drag in this component. */
  position: { x: number; y: number };
  /** Active tab. Updated by tab clicks in this component. */
  activeTab: BlockAuthoringPaletteTab;
  /** Docked side (right edge) vs free-float. Phase K.1 supports float-only;
   *  the value is plumbed for K.4 (docking gesture). */
  docked: "right" | "float";

  /** Close the palette. Triggered by the header's ✕ button. */
  onClose: () => void;
  /** Switch tab. Persists localStorage; parent re-renders the shell. */
  onTabChange: (next: BlockAuthoringPaletteTab) => void;
  /** Drag-end callback for the floating header — emits the new top-left. */
  onPositionChange: (next: { x: number; y: number }) => void;

  /**
   * Phase K.2 / K.3 placement entry-point. Phase K.1 wiring is
   * limited to fire-and-forget — the host logs / dispatches a
   * placeholder action; no real draft session starts yet.
   *
   * Source distinguishes "click" (immediate) vs "drag" (drop on
   * canvas) so K.2 can skip the drag-cancel branch when the user
   * clicked instead of dragging.
   */
  onBeginPlace: (
    kind: BlockAuthoringPlacementKind,
    source: "click" | "drag",
  ) => void;
}

/**
 * Main palette shell. Pure UI; the host owns the persisted state.
 */
export function BlockAuthoringPaletteShell(
  props: BlockAuthoringPaletteShellProps,
): ReactElement | null {
  const t = useT();

  // ── Drag tracking ─────────────────────────────────────────────────
  // We use pointer events instead of native drag-and-drop for the
  // header so the move tracks pixel-perfect (HTML5 drag visually
  // ghosts the element instead of moving it). pointerId allows
  // setPointerCapture so the user can drag past the window edge
  // without losing the gesture.
  const dragRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  // Local position so the move feels responsive without a roundtrip
  // through the host's state. We sync to props.position on prop
  // changes (e.g. reset / docked → float).
  const [livePosition, setLivePosition] = useState(props.position);
  useEffect(() => {
    setLivePosition(props.position);
  }, [props.position]);

  const handleHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      // Don't start a drag when the user clicked the close button.
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-palette-close]")) return;
      const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
      dragRef.current = {
        pointerId: event.pointerId,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
      };
      (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
    },
    [],
  );

  const handleHeaderPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const next = {
        x: Math.max(0, event.clientX - drag.offsetX),
        y: Math.max(0, event.clientY - drag.offsetY),
      };
      setLivePosition(next);
    },
    [],
  );

  const handleHeaderPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      try {
        (event.currentTarget as HTMLDivElement).releasePointerCapture(
          event.pointerId,
        );
      } catch {
        // ignore — capture may have already been released.
      }
      dragRef.current = null;
      props.onPositionChange(livePosition);
    },
    [props, livePosition],
  );

  // ── Item handlers ─────────────────────────────────────────────────
  const handleItemClick = useCallback(
    (kind: BlockAuthoringPlacementKind) => {
      props.onBeginPlace(kind, "click");
    },
    [props],
  );

  const handleItemDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, kind: BlockAuthoringPlacementKind) => {
      // Encode the kind as JSON so the canvas drop target reads it
      // back via dataTransfer.getData(MIME). Phase K.2 owns the drop
      // listener; K.1 just emits the begin-place callback so the
      // host can log / track the drag.
      try {
        event.dataTransfer.setData(
          BLOCK_AUTHORING_DRAG_MIME,
          JSON.stringify(kind),
        );
        event.dataTransfer.effectAllowed = "copy";
      } catch {
        // jsdom (test env) doesn't always implement setData; ignore.
      }
      props.onBeginPlace(kind, "drag");
    },
    [props],
  );

  const visibleItems = useMemo(() => {
    if (props.activeTab === "parameters") {
      return PARAMETER_ITEMS.map((item) => ({
        kind: { tab: "parameters" as const, id: item.id },
        labelKey: item.labelKey,
        icon: { kind: "parameter" as const, id: item.id },
      }));
    }
    if (props.activeTab === "actions") {
      return ACTION_ITEMS.map((item) => ({
        kind: { tab: "actions" as const, id: item.id },
        labelKey: item.labelKey,
        icon: { kind: "action" as const, id: item.id },
      }));
    }
    return PARAMETER_SET_ITEMS.map((item) => ({
      kind: { tab: "parameter-sets" as const, id: item.id },
      labelKey: item.labelKey,
      // Parameter sets reuse the parameter icon of the headline kind
      // — keeps the visual grammar consistent without inventing a
      // dedicated set glyph that would diverge from AutoCAD.
      icon: parameterSetIcon(item.id),
    }));
  }, [props.activeTab]);

  if (!props.open) return null;

  return (
    <aside
      className={
        "block-authoring-palette" +
        (props.docked === "right" ? " is-docked-right" : " is-floating")
      }
      data-testid="block-authoring-palette-shell"
      role="dialog"
      aria-label={t("block_authoring.title")}
      style={{
        left: props.docked === "right" ? undefined : livePosition.x,
        top: props.docked === "right" ? 96 : livePosition.y,
        right: props.docked === "right" ? 0 : undefined,
      }}
    >
      <div
        className="block-authoring-palette-header"
        data-testid="block-authoring-palette-header"
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={handleHeaderPointerUp}
      >
        <span className="block-authoring-palette-title">
          {t("block_authoring.title")}
        </span>
        <button
          type="button"
          className="block-authoring-palette-close"
          data-palette-close
          data-testid="block-authoring-palette-close"
          aria-label={t("block_authoring.close.aria")}
          onClick={props.onClose}
        >
          ✕
        </button>
      </div>

      <BlockAuthoringPaletteShellTabs
        activeTab={props.activeTab}
        onTabChange={props.onTabChange}
      />

      <div
        className="block-authoring-palette-list"
        role="list"
        data-testid={`block-authoring-palette-list-${props.activeTab}`}
      >
        {visibleItems.map((item) => {
          const label = t(item.labelKey as never);
          return (
            <div
              key={`${item.kind.tab}:${item.kind.id}`}
              className="block-authoring-palette-item"
              role="listitem"
              draggable
              data-testid={`block-authoring-palette-item-${item.kind.tab}-${item.kind.id}`}
              data-palette-kind={`${item.kind.tab}:${item.kind.id}`}
              onClick={() => handleItemClick(item.kind)}
              onDragStart={(event) => handleItemDragStart(event, item.kind)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleItemClick(item.kind);
                }
              }}
              tabIndex={0}
            >
              <span className="block-authoring-palette-item-icon">
                <BlockAuthoringIcon icon={item.icon} ariaLabel={label} />
              </span>
              <span className="block-authoring-palette-item-label">{label}</span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/**
 * Map a parameter set id to the icon kind used in its row. Mirrors the
 * "headline" parameter type each set produces so the visual grammar
 * matches the dedicated parameter row above.
 */
function parameterSetIcon(
  id: ParameterSetId,
): { kind: "parameter"; id: ParameterIconKind } {
  switch (id) {
    case "linear-move":
    case "linear-stretch":
      return { kind: "parameter", id: "linear" };
    case "polar-move":
      return { kind: "parameter", id: "polar" };
    case "rotation-set":
      return { kind: "parameter", id: "rotation" };
  }
}
